import { useCallback, useMemo, useState } from "react";
import { NamePromptModal } from "@/react/components/NamePromptModal";
import { FileText, Layers, Calendar, ArrowUpRight, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";
import { useCoverage } from "../context/CoverageContext";
import type { CoverageWorkFile } from "@/react/features/coverage/api/types";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";

function formatDate(d: Date | string | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatRelative(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return formatDate(d);
}

interface WorkFileRowProps {
  file: CoverageWorkFile;
  onOpen: (id: number) => void;
}

const WorkFileRow = ({ file, onOpen }: WorkFileRowProps) => {
  const displayName = file.display_name || file.name || file.title;
  const preview = file.preview_text;
  const cardCount = file.card_count ?? 0;

  return (
    <button
      type="button"
      onClick={() => onOpen(file.id)}
      className={cn(
        "group w-full text-left flex flex-col gap-2 px-4 py-3 border-b border-border/50 transition-colors",
        "hover:bg-muted/40 focus-visible:outline-none focus-visible:bg-muted/40",
        file.archived && "opacity-50"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="size-3.5 shrink-0 text-muted-foreground/60" />
          <span
            className={cn(
              "text-sm font-medium truncate leading-tight",
              file.archived ? "line-through text-muted-foreground" : "text-foreground"
            )}
          >
            {displayName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {file.archived && (
            <span className="text-[10px] text-amber-500 font-medium">Archived</span>
          )}
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60">
            <Layers className="size-2.5" />
            {cardCount}
          </span>
          <ArrowUpRight className="size-3 text-muted-foreground/30 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground/60" />
        </div>
      </div>

      {preview && (
        <p className="text-xs text-muted-foreground/70 leading-relaxed line-clamp-2 pl-5">
          {preview}
        </p>
      )}

      <div className="flex items-center gap-3 pl-5 text-[10px] text-muted-foreground/50">
        {file.creator_name && <span>{file.creator_name}</span>}
        <span className="flex items-center gap-1">
          <Calendar className="size-2.5" />
          {formatRelative(file.updated_at)}
        </span>
      </div>
    </button>
  );
};

export const HubWorkFilesPanel = () => {
  const {
    selectedHub,
    hubWorkFiles,
    handleGenerateWorkFile,
    workFilesLoading,
    workFilesFetching,
  } = useCoverage();
  const { viewManager } = useSwivuBryntumContext();
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const runGenerate = useCallback(
    async (name: string) => {
      if (!selectedHub) return;
      setIsGenerating(true);
      try {
        await handleGenerateWorkFile(selectedHub, name);
        const view = viewManager?.getDefaultViewByType(VIEW_TYPES.workFiles);
        if (view) viewManager?.selectView(view);
      } finally {
        setIsGenerating(false);
      }
    },
    [selectedHub, handleGenerateWorkFile, viewManager]
  );

  const handleOpen = useCallback(
    (id: number) => {
      const view = viewManager?.getDefaultViewByType(VIEW_TYPES.workFiles);
      if (view) {
        viewManager?.selectView(view);
        viewManager?.selectViewAnonymousView(view.id, view.name, view.viewType, {
          workFileId: id,
        });
      }
    },
    [viewManager]
  );

  const activeFiles = useMemo(
    () => hubWorkFiles.filter((f) => !f.archived),
    [hubWorkFiles]
  );
  const archivedFiles = useMemo(
    () => hubWorkFiles.filter((f) => f.archived),
    [hubWorkFiles]
  );

  if (!selectedHub) return null;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-card">
      <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-border">
        <p className="text-sm font-medium text-foreground leading-none flex-1">
          Generated Work Files
        </p>
        {workFilesFetching && !workFilesLoading && (
          <Loader2 className="size-3.5 animate-spin text-muted-foreground/50" aria-label="Refreshing work files" />
        )}
        {hubWorkFiles.length > 0 && (
          <Badge variant="muted" size="xs">
            {hubWorkFiles.length}
          </Badge>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto bg-background">
        {workFilesLoading ? (
          <div className="flex h-full min-h-[10rem] flex-col items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" aria-label="Loading work files" />
            <p className="text-xs">Loading work files…</p>
          </div>
        ) : (
          <div
            className={cn(
              "flex flex-col transition-opacity",
              workFilesFetching && "opacity-70"
            )}
          >
            {hubWorkFiles.length === 0 && !isGenerating ? (
              <div className="flex flex-col items-center justify-center gap-3 h-full min-h-[10rem] px-6 py-8 text-center">
                <div className="rounded-full bg-muted p-3">
                  <FileText className="size-5 text-muted-foreground/40" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">No work files yet</p>
                  <p className="text-xs text-muted-foreground text-balance leading-relaxed">
                    Generate a work file from this hub to see it here.
                  </p>
                </div>
              </div>
            ) : hubWorkFiles.length > 0 ? (
              <div className="flex flex-col">
                {activeFiles.map((file) => (
                  <WorkFileRow key={file.id} file={file} onOpen={handleOpen} />
                ))}
                {archivedFiles.length > 0 && (
                  <>
                    <div className="px-4 py-2 text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider border-b border-border/40 bg-muted/20">
                      Archived
                    </div>
                    {archivedFiles.map((file) => (
                      <WorkFileRow key={file.id} file={file} onOpen={handleOpen} />
                    ))}
                  </>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border bg-card px-4 py-3">
        <Button
          variant="default"
          className="w-full gap-1.5 text-xs font-medium"
          disabled={isGenerating || workFilesLoading}
          onClick={() => setGenerateModalOpen(true)}
        >
          {isGenerating ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
          ) : (
            <Sparkles className="size-3.5 shrink-0" />
          )}
          {isGenerating ? "Generating…" : "Generate Work File"}
        </Button>
      </div>

      <NamePromptModal
        open={generateModalOpen}
        onOpenChange={setGenerateModalOpen}
        title="Generate work file"
        description={
          <>
            Enter a name for the marketing work file generated from{" "}
            <span className="font-medium text-foreground">{selectedHub.name}</span>.
          </>
        }
        fieldLabel="Work file name"
        placeholder="Enter work file name"
        initialValue={selectedHub.name}
        submitLabel="Generate"
        submittingLabel="Starting…"
        onConfirm={runGenerate}
      />
    </div>
  );
};
